/**
 * Design tokens — source unique de vérité (cf. docs/02_DESIGN_SYSTEM.md).
 * Aucune valeur de style en dur ailleurs : tout composant importe d'ici.
 */
/** Largeur maximale du contenu d'un écran. Sert AUSSI de seuil à l'échelle (voir plus bas). */
const LARGEUR_CONTENU_MAX = 1200;
/** Gabarits : compact (téléphone), moyen (tablette), étendu (ordinateur). Voir `gabarit.ts`. */
const SEUIL_GABARIT_MOYEN = 600;
const SEUIL_GABARIT_ETENDU = 1024;

/**
 * ┌─────────────────────────────────────────────────────────────────────────────────────────────┐
 * │  LE NOMBRE À TOURNER. Un seul, et c'est celui-ci.                                           │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Toutes les tailles de TEXTE, d'ESPACEMENT et de RAYON de l'application sont multipliées par ce
 * facteur au-delà du seuil. Le passer de 0,85 à 0,9 ne demande de toucher à rien d'autre : 465
 * déclarations de `fontSize` et 615 d'espacement lisent déjà ce fichier.
 *
 * NE COUVRE PAS LES ICÔNES. Leur taille est passée en prop (`size={24}`) à deux bibliothèques
 * distinctes, en 112 endroits : aucune ne passe par le thème. Voir le rapport de ce lot.
 */
export const ECHELLE_LARGE = 1;

/**
 * Facteur d'échelle, isolé du reste pour être vérifiable.
 *
 * DEUX GARDES, ET LA PREMIÈRE EST STRUCTURELLE. `os !== 'web'` rend l'échelle inatteignable sur
 * appareil quoi qu'il arrive — le mobile ne peut pas rétrécir, même si quelqu'un autorisait un
 * jour le paysage ou la tablette. La seconde, le seuil, est celle qui a un sens visuel : c'est
 * exactement le seuil de la borne de largeur, donc l'échelle s'applique précisément quand le
 * contenu cesse de remplir la fenêtre.
 */
export function facteurEchelle(largeur: number, os: string): number {
  return os === 'web' && largeur > LARGEUR_CONTENU_MAX ? ECHELLE_LARGE : 1;
}

/**
 * ⚠️ CE FICHIER N'IMPORTE PAS `react-native`, ET NE DOIT PAS LE FAIRE.
 *
 * `tailwind.config.ts` importe ces tokens (le thème Tailwind en est dérivé), et NativeWind charge
 * cette configuration via sucrase, dans un contexte Node ordinaire. Y faire entrer `react-native`
 * fait charger son `index.js`, typé Flow : sucrase le lit en mode JavaScript, bute sur son
 * `import type` et le BUILD ENTIER échoue sur « Error loading Metro config », à mille lieues de la
 * vraie cause. D'où la détection ci-dessous, en JavaScript nu.
 *
 * `document` est la frontière : react-native-web tourne dans un navigateur qui en a un, une
 * application native n'en a pas, et Node non plus. C'est exactement ce que `Platform.OS === 'web'`
 * signifierait, sans la dépendance.
 *
 * MESURÉ UNE FOIS, À L'IMPORT. Les feuilles de style sont construites par `StyleSheet.create` au
 * chargement de chaque module : il n'existe aucun moment plus tard où une nouvelle valeur serait
 * relue. Conséquence à connaître : redimensionner la FENÊTRE d'un navigateur ne rebascule pas
 * l'échelle, il faut recharger la page. C'est le prix de « un seul nombre dans le thème » plutôt
 * qu'un contexte React traversant les trente-quatre écrans.
 *
 * Hors navigateur — appareil, Node, Jest — `facteurEchelle` rend 1 et chaque token ci-dessous vaut
 * son entier d'origine, au bit près : `Math.round(n * 1) === n`.
 */
export const SUR_LE_WEB = typeof document !== 'undefined';
const FACTEUR = facteurEchelle(SUR_LE_WEB ? window.innerWidth : 0, SUR_LE_WEB ? 'web' : 'natif');

/**
 * Vrai quand l'application s'affiche sur une fenêtre web d'au moins un gabarit « moyen » : jamais
 * sur appareil. Il commande les bornes de colonne de formulaire et de commande (plus bas), qui ne
 * sont pas des réductions d'échelle : elles ne servent qu'à ne pas étirer un champ sur 1000 px.
 *
 * MESURÉ UNE FOIS, À L'IMPORT, comme l'échelle : les feuilles de style sont figées au chargement.
 * La disposition des écrans, elle, réagit à la fenêtre en direct (`useGabarit`).
 */
export const GRAND_ECRAN = SUR_LE_WEB && window.innerWidth >= SEUIL_GABARIT_MOYEN;

const e = (n: number) => Math.round(n * FACTEUR);

/**
 * Échelonne une dimension passée en PROP plutôt qu'en feuille de style — la taille d'une icône.
 *
 * Réservée à `components/icons`. Ailleurs, `size={…}` ne désigne pas forcément une icône :
 * `IconPill`, `BrandLogo`, `ScoreRing`, `GoogleLogo` et `Sparkle` portent la même prop et en
 * dérivent leur géométrie interne. Les échelonner est une décision distincte, pas un automatisme.
 */
export const taille = e;

/** Échelonne les nombres d'un objet de tokens en préservant ses clés. */
type Nombres<T> = { [K in keyof T]: number };
const echelonner = <T extends Record<string, number>>(base: T): Nombres<T> =>
  Object.fromEntries(Object.entries(base).map(([cle, valeur]) => [cle, e(valeur)])) as Nombres<T>;

export const colors = {
  // ─── Marque Horméia (charte 2026) — noms clairs ───
  cyan: '#5CBCE5', // énergie digitale : dégradés, icônes, boutons secondaires
  electric: '#578DEA', // confiance / tech : logo, UI, fonds
  violet: '#A553C7', // ambition / créativité : premium, accents
  corail: '#ED8745', // réussite / action : flèche, CTA, conversion
  night: '#071B4D', // textes, titres, fonds premium

  // ─── Alias historiques (mêmes rôles, valeurs charte ; renommage en lot de suivi) ───
  blueNight: '#071B4D', // fonds premium très sombres (ex. génération CV)
  blueDeep: '#071B4D', // fonds onboarding sombres, héros
  bluePrimary: '#578DEA', // = electric : boutons primaires, liens, focus
  blueNav: '#578DEA', // FAB central (fond foncé, icône blanche lisible ; dégradé en phase 2)
  blueSoft: '#E7EFFC', // tint électrique clair (tags/zones bleutées)

  // Accent "green" → cyan ; accent "lime" → violet (clés conservées pour ne rien casser)
  green: '#5CBCE5', // = cyan
  greenLime: '#A553C7', // = violet
  greenSoft: '#E6F5FB', // tint cyan
  limeSoft: '#F3EAF8', // tint violet

  // Encre — cartes sombres → bleu nuit
  ink: '#071B4D',
  inkSoft: '#102554', // variante un peu plus claire (bordures sombres)

  // Neutres (charte)
  white: '#FFFFFF',
  grey50: '#F5F7FA', // gris clair charte : fonds d'écran/sections
  grey100: '#EDF0F6',
  grey200: '#E1E6EF', // bordures, dividers
  grey400: '#9AA3B4',
  grey500: '#5B6475', // gris texte charte : texte secondaire, légendes
  grey700: '#3B4254',
  grey900: '#071B4D', // texte principal / titres → bleu nuit (charte)

  // Sémantique FONCTIONNEL (conservé : clarté validation/erreur)
  success: '#16A34A',
  warning: '#F59E0B',
  error: '#DC2626',
  info: '#578DEA', // info → électrique

  // Tints sémantiques clairs (fonds de blocs : bilan d'entretien, etc.)
  successSoft: '#E6F4EA',
  warningSoft: '#FEF4E6',
  infoSoft: '#E7EFFC',

  // Tint corail doux (pastilles/cartes corail) — les tints électrique/cyan/violet
  // existent déjà via blueSoft/greenSoft/limeSoft.
  corailSoft: '#FCEBDD',

  // Violet du LOGO, échantillonné dans assets/logo-mark.png (moyenne pondérée des pixels violets
  // sur 11 200 échantillons) et non inventé. Il tire vers le bleu, là où le `violet` de la charte
  // (#A553C7) tire vers le rose : les deux coexistent, celui-ci sert aux surfaces qui doivent
  // rappeler le logo.
  violetLogo: '#8C40F8',

  // Violet TRANSLUCIDE (et non un tint opaque comme limeSoft) : le fond de l'écran transparaît,
  // ce qui pose le bloc sur la page au lieu de le coller dessus. Sur la teinte du LOGO et à 9 %
  // — au-delà, le texte gris700 perd en contraste.
  violetVeil: 'rgba(140, 64, 248, 0.09)',

  // Voile posé derrière une feuille ouverte depuis le bas : la valeur déjà employée par
  // SearchTargetSheet et BackgroundPicker, nommée ici.
  voile: 'rgba(0, 0, 0, 0.35)',
  // Reflet cyan translucide : balayage de l'étape en cours (écran de préparation des candidatures).
  refletCyan: 'rgba(92, 188, 229, 0.18)',

  // État désactivé (boutons) : fond neutre + texte gris
  disabledBg: '#E1E6EF',
  disabledText: '#9AA3B4',

  // ─────────────────── Teintes ÉCHANTILLONNÉES sur la maquette Carrière ───────────────────
  // Prélevées au pixel dans `nouvelle-page-carriere.jpeg`, pas estimées à l'œil. Elles sont plus
  // saturées que la charte 2026 d'origine, dont les teintes rendaient les grandes surfaces fades.

  // Bleu marine des TITRES. `night` (#071B4D) reste le gris900 des textes courants ; celui-ci est
  // plus clair et moins saturé, et c'est lui que la maquette pose sur les grands titres.
  navy: '#1C214B',

  // Couleurs de FEATURE, pour des surfaces pleines (fond de carte). Voir featureAccents.ts.
  blueVivid: '#2A4FD9', // CV — échantillonné sur la carte active
  violetVivid: '#7A54E7', // LinkedIn — échantillonné sur la carte de droite
  magenta: '#DE4FA1', // lettre — prélevé dans le dégradé de marque
  coralVivid: '#F75F6B', // entretien — extrémité chaude du dégradé de marque
  navyDeep: '#29346C', // offres — échantillonné sur la carte de gauche
  indigo: '#5C72F9', // coaching — extrémité froide du dégradé de marque

  // Fonds clairs correspondants. DÉRIVÉS, non échantillonnés : la maquette ne montre aucune
  // pastille claire. Ils ne servent qu'au schéma d'avant connexion (FeatureWheel).
  blueVividSoft: '#E4E9FB',
  violetVividSoft: '#EEE7FD',
  magentaSoft: '#FBE6F2',
  coralVividSoft: '#FEE9EB',
  navyDeepSoft: '#E4E7F0',
  indigoSoft: '#E7EAFE',

  // Refonte (additif, rien d'existant ne change). Le bleu d'origine ne fait que 3,3:1 sur blanc :
  // l'accent profond (4,8:1) porte les textes et états actifs, son encre (6,3:1) les textes courants.
  electricDeep: '#3F6FD1',
  electricInk: '#2F5BB8',
  // Textes secondaires et piste d'anneau sur fond bleu nuit.
  blancAtenue: 'rgba(255, 255, 255, 0.7)',
  pisteSombre: 'rgba(255, 255, 255, 0.14)',
  // Voiles blancs des boutons et cartes posés sur le bleu nuit (accueil, bandeau de connexion).
  surfaceNuit: 'rgba(255, 255, 255, 0.08)',
  surfaceNuitForte: 'rgba(255, 255, 255, 0.12)',
  bordureNuit: 'rgba(255, 255, 255, 0.22)',
  // Contour des boutons secondaires clairs (« Importer », « Aperçu ») : un bleu pâle, pas un gris.
  bordureClaire: '#CBD6EE',
  // Pêche : le titre « Fondateur d'Horméia » posé sur le bleu nuit de la carte du coach.
  pecheNuit: '#F4B183',
  // Coche de confirmation posée sur le bleu nuit (« Enregistré dans Mes CV »).
  vertSurNuit: '#6EE7A0',
  // Voix de l'interlocuteur (entretien) : rose clair et rose profond de l'avatar et des barres de son.
  roseVoix: '#F58A98',
  roseVoixProfond: '#E5566D',
  // Carte d'aperçu du profil LinkedIn (audit) : violet profond de la bannière et trait de balayage.
  violetProfil: '#7A2FD6',
} as const;

/**
 * Dégradé officiel : indigo → violet → magenta → corail vif.
 *
 * ÉCHANTILLONNÉ, pas choisi. Trois relevés indépendants sur la maquette — le mot « appellent. » du
 * titre, le FAB central et le trait actif du carrousel — donnent la même course : #5C72F9 →
 * #925EE4 → #DE4FA1 → #F75F6B. L'ancien jeu (cyan → électrique → violet → corail) partait d'un
 * cyan très pâle et finissait en ORANGE : sur un mot court, les deux premiers arrêts, presque
 * identiques, occupaient la moitié de la course et le dégradé se lisait comme un aplat bleu.
 *
 * Source unique (boutons importants, titres, bannières, FAB, barres de progression).
 */
export const gradients = {
  brand: [colors.indigo, colors.violetVivid, colors.magenta, colors.coralVivid],
  // Accent de l'écran de préparation, dans l'esprit de ScoreRing. Sur bleu nuit : cyan 7,9:1,
  // électrique 5,2:1 (AA). Un texte posé dessus est en bleu nuit — le blanc n'y tient que 3,3:1.
  accent: [colors.cyan, colors.electric],
  // Appels à l'action des écrans refaits : trois teintes déjà dans la palette (logo), sans les
  // quatre arrêts de `brand`. Ne le remplace pas.
  cta: [colors.electric, colors.violetLogo, colors.corail],
  // Texte en dégradé sur fond bleu nuit. `brand` démarre sur un indigo trop sombre pour s'y lire ;
  // ces quatre arrêts tiennent tous 4,5:1 sur #071B4D (mesurés, voir refonteLot1.test.ts).
  surNuit: [colors.cyan, '#8C9BFF', '#C86FD8', colors.corail],
  // Fond des onglets Offres et Carrière : bleu pâle en haut, lavande puis rose très doux en bas.
  // Remplace le gris presque blanc et les cercles violets ; les cartes blanches y ressortent.
  aube: ['#D3E1FA', '#E2E5F9', '#EEE8F5'],
  // Carte pédagogique de la lettre : un violet profond qui se fond dans le bleu nuit.
  nuitLettre: ['#3B1670', colors.night],
  // Carte de dépôt du CV (Note ton CV) : un bleu roi qui se fond dans le bleu nuit.
  nuitCv: ['#0D2F78', colors.night],
  // Avatar de l'interlocuteur et barres de son de la carte d'entretien.
  voixEntretien: [colors.roseVoix, colors.roseVoixProfond],
  sonEntretien: [colors.roseVoix, colors.corail],
  // Bannière de l'aperçu de profil LinkedIn (audit).
  profilLinkedin: [colors.violetProfil, colors.electric],
} as const;

const TYPOGRAPHIE_BASE = {
  // Police = système (SF Pro iOS / Roboto Android) : pas de fontFamily codée.
  display: { size: 30, weight: '700', lineHeight: 36 }, // gros titres écran
  h1: { size: 24, weight: '700', lineHeight: 30 },
  h2: { size: 20, weight: '600', lineHeight: 26 },
  h3: { size: 17, weight: '600', lineHeight: 22 },
  body: { size: 15, weight: '400', lineHeight: 22 },
  bodyMd: { size: 15, weight: '500', lineHeight: 22 },
  small: { size: 13, weight: '400', lineHeight: 18 },
  caption: { size: 11, weight: '500', lineHeight: 14, letterSpacing: 0.4 }, // labels majuscules
} as const;

/**
 * `weight` GARDE SON TYPE LITTÉRAL, les tailles s'élargissent en `number`.
 *
 * Ce n'est pas une coquetterie de typage : `fontWeight` n'accepte que des littéraux ('400', '700'…)
 * et un `string` nu y provoquerait une erreur dans chacun des composants qui lit `typography`. Le
 * type dit donc exactement la vérité — la graisse est figée, les tailles sont calculées.
 */
type Echelonne<T> = { [K in keyof T]: { [P in keyof T[K]]: P extends 'weight' ? T[K][P] : number } };

export const typography = Object.fromEntries(
  Object.entries(TYPOGRAPHIE_BASE).map(([cle, style]) => [
    cle,
    {
      ...style,
      size: e(style.size),
      lineHeight: e(style.lineHeight),
      ...('letterSpacing' in style ? { letterSpacing: style.letterSpacing * FACTEUR } : {}),
    },
  ]),
) as Echelonne<typeof TYPOGRAPHIE_BASE>;

export const spacing = echelonner({ xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 });

export const radius = {
  ...echelonner({
    sm: 8,
    md: 12,
    lg: 16, // cartes standard
    xl: 20, // grandes cartes feature
  }),
  // NON échelonné : ce n'est pas une mesure mais une sentinelle « entièrement arrondi ». La réduire
  // ne change rien au rendu et ferait perdre son sens au nombre.
  pill: 999, // chips, boutons arrondis
};

export const screenPadding = e(16); // marge latérale standard des écrans

/**
 * Formes de la refonte : hauteurs de commande, rayons et diamètres d'anneau repris des maquettes.
 *
 * Export à part et non dans `tokens` : l'agrégat est figé par `tokens.test.ts`, et `radius` est
 * comparé tel quel par `echelle.test.ts`. Mêmes règles d'échelle que le reste : valeurs mobile
 * inchangées, réduites sur fenêtre large.
 */
export const formes = {
  hauteur: echelonner({ xl: 58, lg: 56, md: 52, sm: 40 }),
  rayon: echelonner({ xl: 32, lg: 28, md: 16, sm: 12 }),
  anneau: echelonner({ sm: 80, md: 88, lg: 104 }),
  interrupteur: echelonner({ largeur: 46, hauteur: 28, pouce: 22 }),
  /** Cible tactile minimale (44 pt) : une commande ne descend jamais dessous, même réduite. */
  cible: 44,
} as const;

/**
 * BORNES DE LARGEUR — elles n'existent que pour les fenêtres larges (web).
 *
 * Aucune n'est atteignable sur mobile, et c'est une garantie STRUCTURELLE, pas un espoir :
 * `app.json` fixe `orientation: portrait` et `ios.supportsTablet: false`, ce qui plafonne la
 * largeur à 430 pt (iPhone Pro Max). Les trois valeurs ci-dessous sont très au-dessus, donc
 * inertes sur téléphone — le rendu mobile ne peut pas bouger. Lever l'une de ces deux contraintes
 * d'`app.json` (paysage, ou support tablette) rendrait ces bornes actives sur appareil : c'est le
 * moment où il faudra les revoir.
 */
export const layout = {
  /** Largeur maximale du CONTENU d'un écran, au-delà de laquelle il se centre. */
  contentMaxWidth: LARGEUR_CONTENU_MAX,
  /**
   * Largeur maximale de la RANGÉE de la barre d'onglets. Plus étroite que le contenu : quatre
   * onglets et un bouton central répartis sur 1200 px seraient épars et illisibles. La surface de
   * la barre, elle, reste pleine largeur (voir `BottomNav`).
   */
  tabBarMaxWidth: 560,
  /**
   * Seuil au-delà duquel le carrousel de Carrière abandonne le dimensionnement proportionnel.
   * Choisi bien au-dessus de 430 pt pour rester hors d'atteinte du mobile, et bien en dessous de
   * `contentMaxWidth` pour s'appliquer dès qu'une fenêtre cesse d'être étroite.
   */
  carouselFixedFrom: 700,
  /**
   * Largeur d'une carte du carrousel AU-DELÀ du seuil, en pixels. Au seuil, la règle
   * proportionnelle donnerait 406 px et continuerait de croître ; au-delà, la carte s'arrête ici.
   */
  carouselCardWidth: 380,
  /**
   * Largeur maximale d'une COMMANDE — bouton principal, champ de saisie.
   *
   * Un bouton large de 1200 px n'est plus un bouton : on ne sait plus où viser, et la cible cesse
   * d'être lue comme un objet. La borne de contenu ne suffit donc pas, elle borne la COLONNE ;
   * celle-ci borne les objets DANS la colonne.
   */
  controlMaxWidth: 420,
  /**
   * Largeur MINIMALE d'un élément de grille. C'est elle, et elle seule, qui décide du nombre de
   * colonnes : on en met autant que la place en autorise sans descendre sous cette largeur.
   * 380 px est la largeur en dessous de laquelle une carte d'article cesse d'être lisible — titre
   * sur quatre lignes, source et date qui se chevauchent.
   */
  gridMinItemWidth: 380,
  /**
   * Largeur maximale d'une COLONNE DE FORMULAIRE.
   *
   * Plus étroite que la colonne de contenu, et pour une raison différente : 1200 px est la limite
   * du confort de LECTURE, 520 celle du confort de SAISIE. Un champ de 1150 px de large oblige
   * l'œil à traverser l'écran entre son libellé et son contenu, et rend la ligne de saisie
   * impossible à suivre.
   */
  formMaxWidth: 520,
  /**
   * GABARITS. En dessous de `seuilMoyen`, l'application est exactement celle du téléphone. Entre
   * les deux, une tablette (rail d'icônes) ; au-delà de `seuilEtendu`, un ordinateur (barre
   * latérale). Voir `src/theme/gabarit.ts`, qui porte la règle et sa garantie sur appareil.
   */
  seuilMoyen: SEUIL_GABARIT_MOYEN,
  seuilEtendu: SEUIL_GABARIT_ETENDU,
  /** Largeur de la barre latérale (gabarit étendu) et du rail d'icônes (gabarit moyen). */
  barreLateraleLargeur: 252,
  railLargeur: 84,
  /** Volet de gauche d'une vue en deux volets (liste, préparation), en gabarit étendu. */
  voletPrincipalLargeur: 400,
  /** Largeur maximale de la zone de contenu d'une page, à côté de la barre latérale. */
  pageMaxWidth: 1240,
  /** Diamètre maximal de l'orbite de l'accueil sur ordinateur. */
  orbiteMax: 560,
} as const;

/**
 * COLONNE DE FORMULAIRE — à poser sur le CONTENEUR de contenu d'un écran de saisie, pas sur ses
 * champs.
 *
 * C'est le même raisonnement que la borne globale : ce qu'on veut borner est la colonne, pas
 * chacun de ses éléments. Trente-six `TextInput` bornés un à un auraient donné trente-six endroits
 * à corriger, et surtout n'auraient pas borné ce qui les accompagne — libellés, boutons, messages
 * d'erreur — qui doivent rester alignés avec eux.
 *
 * Vide sur mobile, comme `controleLarge` : aucune clé, rien à évaluer.
 */
export const colonneFormulaire = GRAND_ECRAN
  ? ({ width: '100%', maxWidth: layout.formMaxWidth, alignSelf: 'center' } as const)
  : ({} as const);

/**
 * Au-delà de trois colonnes, une carte d'article redevient une vignette et la grille se lit comme
 * un catalogue. La borne de contenu (1200) en autorise trois de 380 : c'est le maximum voulu, pas
 * une limite technique.
 */
export const COLONNES_MAX = 3;

/**
 * NOMBRE DE COLONNES D'UNE GRILLE — le seul endroit où cette règle est écrite.
 *
 * Trois écrans en ont besoin (News, liste de CV, liste de lettres) et AUCUN ne les rend de la même
 * façon : News passe par `numColumns` d'une `FlatList`, les deux autres par un `.map()` dans une
 * `ScrollView`. Ce qui se factorise n'est donc pas le rendu mais la DÉCISION — et c'est elle qu'on
 * voudra ajuster.
 *
 * `1` HORS DU WEB, structurellement : sur appareil, la fonction sort avant tout calcul. La mise en
 * page mobile ne peut pas devenir une grille par un accident d'arithmétique, et les écrans
 * rendent alors exactement l'arbre qu'ils rendaient avant ce lot.
 */
export function colonnesGrille(largeurDisponible: number, surLeWeb: boolean = SUR_LE_WEB): number {
  if (!surLeWeb) return 1;
  const tiennent = Math.floor(largeurDisponible / layout.gridMinItemWidth);
  return Math.min(COLONNES_MAX, Math.max(1, tiennent));
}

/**
 * LARGEUR PROPRE D'UNE COMMANDE SUR GRAND ÉCRAN — à poser sur l'élément qui reçoit l'appui, jamais
 * sur son fond.
 *
 * Borner le fond dégradé d'un bouton plutôt que son `Pressable` laisserait une zone cliquable de
 * 1200 px autour d'un visuel de 420 : on cliquerait dans le vide apparent et ça fonctionnerait,
 * ce qui est pire qu'un bouton trop large.
 *
 * VIDE SUR MOBILE, littéralement : l'objet ne contient aucune clé, aucun style ne change, et la
 * garantie ne repose pas sur une comparaison de nombres qui pourrait se retrouver fausse.
 * `width: '100%'` fait occuper toute la place disponible jusqu'à la borne, puis le centrage prend
 * le relais.
 */
export const controleLarge = GRAND_ECRAN
  ? ({ width: '100%', maxWidth: layout.controlMaxWidth, alignSelf: 'center' } as const)
  : ({} as const);

export const shadow = {
  card: {
    // cartes sur fond clair
    shadowColor: '#071B4D',
    shadowOpacity: 0.06,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 3,
  },
  floating: {
    // FAB, éléments flottants
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
} as const;

export const tokens = { colors, typography, spacing, radius, screenPadding, shadow } as const;
